import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Package, Plus } from 'lucide-react';
import { DashboardLayout } from '../common/page_component/index.js';
import ListPageHeader from '../common/ListPageHeader.js';
import Button from '../common/Button.js';
import Loader from '../common/Loader.js';
import Alert from '../common/Alert.js';
import { mlSheetService } from '../../services/mlSheetService.js';
import useMlCreationPaidSettlement from '../../hooks/useMlCreationPaidSettlement.js';
import { getUnremittedPaidShipments, isShipmentDisabledForMl } from '../../utils/mlPaidSettlementRules.js';
import { useRequirePaidSettlementBeforeMl } from '../../hooks/useRequirePaidSettlementBeforeMl.js';
import MlPaidSettlementGate from './MlPaidSettlementGate.js';
import { canAddMlSheetShipments } from '../../utils/networkMode.js';
import { useAuth } from '../../context/AuthContext.js';
import { listIdLinkClass } from '../common/uiClasses.js';

const AddMlSheetShipments = () => {
  const { mlSheetId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sheet, setSheet] = useState(null);
  const [shipments, setShipments] = useState([]);
  const [selected, setSelected] = useState([]);
  const [search, setSearch] = useState('');
  const {
    loaded: mlBlockLoaded,
    mlCreationBlock,
    dues,
    agentDues,
    isHubAdmin,
    hardBlocked: mlHardBlocked,
    showGate: showPaidSettlementGate,
    settlementPath,
    settleOpen,
    setSettleOpen,
    refresh: refreshMlSettlement,
  } = useMlCreationPaidSettlement(user);

  const requiresPaidBeforeMl = useRequirePaidSettlementBeforeMl(user);
  const shipmentDisabledForMl = (shipment) => isShipmentDisabledForMl(shipment, user, requiresPaidBeforeMl);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const response = await mlSheetService.getPickupAddableShipments(mlSheetId);
      setSheet(response.data?.ml_sheet || null);
      setShipments(response.data?.shipments || []);
      setSelected([]);
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to load shipments');
      navigate('/ml-sheets');
    } finally {
      setLoading(false);
    }
  }, [mlSheetId, navigate]);

  useEffect(() => {
    if (!canAddMlSheetShipments(user)) {
      Alert.error('Add shipment is not available for this role');
      navigate('/ml-sheets');
      return undefined;
    }
    load();
    return undefined;
  }, [user, load, navigate]);

  const visibleShipments = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return shipments;
    return shipments.filter((shipment) => {
      const hay = [
        shipment.shipment_id,
        shipment.sender_location,
        shipment.receiver_location,
        shipment.sender_contact,
        shipment.receiver_contact,
      ].join(' ').toLowerCase();
      return hay.includes(term);
    });
  }, [shipments, search]);

  const toggle = (shipmentId) => {
    if (!mlBlockLoaded || mlHardBlocked) return;
    const shipment = shipments.find((s) => s.shipment_id === shipmentId);
    if (shipment && shipmentDisabledForMl(shipment)) {
      Alert.error('This Paid shipment has unremitted collections. Settle it first.');
      return;
    }
    setSelected((prev) => (
      prev.includes(shipmentId)
        ? prev.filter((id) => id !== shipmentId)
        : [...prev, shipmentId]
    ));
  };

  const handleAdd = async () => {
    if (!mlBlockLoaded) return;
    if (mlHardBlocked) {
      Alert.error(mlCreationBlock.reason || 'ML sheet creation is temporarily blocked');
      return;
    }
    const blockedSelected = requiresPaidBeforeMl
      ? getUnremittedPaidShipments(shipments, selected)
      : [];
    if (blockedSelected.length > 0) {
      Alert.error('Selected Paid shipment(s) have unremitted collections. Settle them first.');
      return;
    }
    if (!selected.length) {
      Alert.error('Select at least one shipment');
      return;
    }
    setSaving(true);
    try {
      await mlSheetService.addPickupShipments(mlSheetId, selected);
      Alert.success(`${selected.length} shipment(s) added to the ML sheet`);
      navigate(`/ml-sheets/view/${mlSheetId}`);
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to add shipments');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20">
          <Loader fullScreen={false} message="Loading shipments..." />
        </div>
      </DashboardLayout>
    );
  }

  const addDisabled = !mlBlockLoaded || mlHardBlocked || !selected.length;

  return (
    <DashboardLayout>
      <div className="max-w-full mx-auto pb-24 md:pb-6">
        <ListPageHeader title="Add Shipment" icon={Package} backTo="/ml-sheets" className="mb-4 sm:mb-6" />

        <MlPaidSettlementGate
          mlCreationBlock={mlCreationBlock}
          dues={dues}
          agentDues={agentDues}
          show={showPaidSettlementGate}
          hardBlocked={mlHardBlocked}
          settleOpen={settleOpen}
          setSettleOpen={setSettleOpen}
          onSettleSubmitted={refreshMlSettlement}
          settlementPath={settlementPath}
          allowSettle={!isHubAdmin}
          className="mb-4"
        />

        {sheet && (
          <div className="bg-white rounded-xl shadow-sm p-4 mb-4 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 text-sm">
            <div>
              <p className="text-xs text-gray-500">ML Sheet</p>
              <p className="font-semibold break-all">{sheet.ml_sheet_id}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Vehicle</p>
              <p className="font-semibold">{sheet.vehicle_number || 'N/A'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Status</p>
              <p className="font-semibold">{sheet.status}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Shipments on sheet</p>
              <p className="font-semibold">{(sheet.shipment_ids || []).length}</p>
            </div>
          </div>
        )}

        <div className="bg-white rounded-xl shadow-sm p-4 mb-4">
          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
            Search shipment
          </label>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="GC number, location, or mobile"
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400"
          />
        </div>

        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          {visibleShipments.length === 0 ? (
            <p className="p-6 text-sm text-gray-500">No booked shipments available to add.</p>
          ) : (
            <ul className="divide-y">
              {visibleShipments.map((shipment) => {
                const checked = selected.includes(shipment.shipment_id);
                const paidBlocked = shipmentDisabledForMl(shipment);
                return (
                  <li key={shipment.shipment_id}>
                    <label className={`flex items-start gap-3 p-4 ${paidBlocked || mlHardBlocked ? 'opacity-60' : 'cursor-pointer hover:bg-gray-50'}`}>
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={!mlBlockLoaded || mlHardBlocked || paidBlocked}
                        onChange={() => toggle(shipment.shipment_id)}
                        className="mt-1 w-4 h-4 shrink-0"
                      />
                      <div className="min-w-0 flex-1 space-y-1">
                        <p className={`${listIdLinkClass} text-sm sm:text-base`}>{shipment.shipment_id}</p>
                        <p className="text-sm text-gray-700">
                          {shipment.sender_location || 'From'} → {shipment.receiver_location || 'To'}
                        </p>
                        <p className="text-sm text-gray-700">
                          ₹{shipment.total_amount || shipment.freight_charges || 0}
                        </p>
                        <p className="text-xs text-gray-500 break-all">
                          {shipment.sender_contact} / {shipment.receiver_contact}
                        </p>
                      </div>
                    </label>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="hidden md:block mt-4">
          <Button
            icon={Plus}
            loading={saving}
            disabled={addDisabled}
            onClick={handleAdd}
          >
            Add selected ({selected.length})
          </Button>
        </div>

        <div className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-gray-200 px-4 py-3 shadow-[0_-4px_12px_rgba(0,0,0,0.06)]">
          <Button
            icon={Plus}
            loading={saving}
            disabled={addDisabled}
            onClick={handleAdd}
            className="w-full"
          >
            Add selected ({selected.length})
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AddMlSheetShipments;
